import { useMutation } from '@tanstack/react-query'
import axiosClient from '../../axios'
import { IAnalysisMalaria } from '../../../types/ia-analysis'

interface DirectMalariaAnalysisResponse {
  analysis: IAnalysisMalaria
  imageUrl: string
  slideId?: string
  debugImages?: Record<string, string>
}

/**
 * Direct Lab malaria analysis without patient creation
 * Uses /api/analysis/malaria/direct endpoint
 */
export const useDirectLabMalariaAnalysis = () => {
  const directAnalysis = useMutation({
    mutationKey: ['directLabMalariaAnalysis'],
    mutationFn: async ({
      file,
      slideId,
      existingImageUrl,
      debugImages,
    }: {
      file?: File
      slideId: string
      existingImageUrl?: string
      debugImages?: boolean
    }): Promise<DirectMalariaAnalysisResponse> => {
      if (existingImageUrl) {
        console.log('🔄 Reanalyzing existing image:', existingImageUrl, 'Slide ID:', slideId)
      } else {
        console.log('🔬 Direct Lab malaria analysis:', file?.name, 'Slide ID:', slideId)
      }

      const formData = new FormData()

      // For reanalysis, send existingImageUrl instead of uploading a new file
      if (existingImageUrl) {
        formData.append('existingImageUrl', existingImageUrl)
        // Send a dummy file to satisfy multer (it expects a file)
        const dummyFile = new File([''], 'dummy.jpg', { type: 'image/jpeg' })
        formData.append('image', dummyFile)
      } else if (file) {
        formData.append('image', file)
      }

      formData.append('slideId', slideId)
      if (debugImages) {
        formData.append('debugImages', 'true')
      }

      const response = await axiosClient.post<DirectMalariaAnalysisResponse>(
        '/analysis/malaria/direct',
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )

      console.log('✅ Analysis result:', response.data)
      return response.data
    },
  })

  return directAnalysis
}

/**
 * Update bounding boxes for a malaria analysis image
 */
export const useUpdateMalariaBoxes = () => {
  const updateBoxes = useMutation({
    mutationKey: ['updateMalariaBoxes'],
    mutationFn: async ({
      imageId,
      positiveBoxes,
      negativeBoxes,
    }: {
      imageId: string
      positiveBoxes: number[][]
      negativeBoxes: number[][]
    }): Promise<{ analysis: IAnalysisMalaria }> => {
      console.log('💾 Updating malaria boxes for image:', imageId)

      const response = await axiosClient.patch<{ analysis: IAnalysisMalaria }>(
        `/lab-analysis/malaria/image/${imageId}/boxes`,
        {
          positiveBoxes,
          negativeBoxes,
        }
      )

      console.log('✅ Boxes updated successfully')
      return response.data
    },
  })

  return updateBoxes
}
